import { CloseSVG, EditSVG } from 'common/assets';
import { themedStyled } from 'common/styles/styletron.theme';
import DeleteButton from 'components/common/delete-button';
import { FormState, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import {
  ActionContainer,
  ButtonContainer,
  TopFormContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext } from 'react-hook-form';

interface FormHeaderProps {
  data?: any;
  onDelete?: () => void;
  title: string | React.ReactNode;
  isDeleteLoading?: boolean;
  submitComponent?: React.ReactNode;
  noDelete?: boolean;
  onRenderComponent?: React.ReactNode;
  onRenderRightComponent?: React.ReactNode;
  noSubmit?: boolean;
  noEdit?: boolean;
  noCancel?: boolean;
  deleteText?: string;
  deleteIcon?: React.ReactNode;
  onCancel?: () => void;
}

export default function FormHeader(props: FormHeaderProps) {
  const { t } = useTranslation();
  const {
    data,
    onDelete,
    title,
    isDeleteLoading,
    submitComponent,
    noDelete,
    onRenderComponent,
    onRenderRightComponent,
    noSubmit,
    noEdit,
    noCancel,
    deleteText,
    onCancel,
    deleteIcon,
  } = props;
  const formMethod = useFormContext<any>();
  const {
    formState: { isSubmitting },
    clearErrors,
  } = formMethod;
  return (
    <TopFormContainer>
      <Title variant="HeadingXSmall">{title}</Title>
      <FormState>
        {({ editable, setIsEditable }) => (
          <ButtonContainer $style={{ alignItems: 'center' }}>
            {onRenderComponent}
            {data && (
              <>
                {!noDelete && (
                  <DeleteButton
                    onClick={onDelete}
                    isLoading={isDeleteLoading}
                    text={deleteText}
                    icon={deleteIcon}
                  />
                )}
                {!noEdit && (
                  <>
                    {editable || isSubmitting ? (
                      !noCancel ? (
                        <ButtonContainer
                          $style={{ marginLeft: noDelete ? '0px' : '16px' }}
                        >
                          <Button
                            onClick={() => {
                              if (onCancel) {
                                onCancel();
                              } else {
                                formMethod.reset();
                                setIsEditable(false);
                                clearErrors();
                              }
                            }}
                            buttonType="secondary"
                            startEnhancer={(color, size) => (
                              <CloseSVG
                                width={size}
                                height={size}
                                color={color}
                              />
                            )}
                            disabled={isSubmitting}
                          >
                            {t('common:cancel')}
                          </Button>
                        </ButtonContainer>
                      ) : (
                        <></>
                      )
                    ) : (
                      <ButtonContainer
                        $style={{
                          marginLeft:
                            noDelete && !onRenderComponent ? '0px' : '16px',
                        }}
                      >
                        <Button
                          onClick={() => setIsEditable(true)}
                          startEnhancer={(color, size) => (
                            <ActionContainer $style={{ marginRight: '8px' }}>
                              <EditSVG size={size} color={color} />
                            </ActionContainer>
                          )}
                        >
                          {t('common:edit')}
                        </Button>
                      </ButtonContainer>
                    )}
                  </>
                )}
              </>
            )}
            {onRenderRightComponent}
            {!noSubmit && (
              <FormState>
                {({ editable }) =>
                  editable || isSubmitting ? (
                    <ButtonContainer
                      $style={{
                        marginLeft: noDelete && !data ? '0px' : '16px',
                      }}
                    >
                      {submitComponent ? (
                        submitComponent
                      ) : (
                        <Input type="submit" />
                      )}
                    </ButtonContainer>
                  ) : (
                    <></>
                  )
                }
              </FormState>
            )}
          </ButtonContainer>
        )}
      </FormState>
    </TopFormContainer>
  );
}

const Title = themedStyled(Text, {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
});
